<template>
    <div>
        <v-card>
            <v-toolbar outlined flat>
                <p class="line" style="margin: 0"></p>
                <v-toolbar-title class="pl-4 subtitle-1 font-weight-bold ma-0 primary--text" style="white-space: normal;">
                    Reportes
                </v-toolbar-title>

                <v-spacer></v-spacer>
                
                <v-btn icon v-if="open" @click="open = false">
                    <v-icon>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn icon v-else @click="open = true">
                    <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
            </v-toolbar>
            <v-toolbar flat v-if="open" dense>
                <div style="width: 200px">
                    <v-select
                        v-model="meter"
                        :items="meters"
                        item-text="name"
                        item-value="id"
                        class="text-caption font-weight-bold mr-3"
                        flat
                        outlined
                        dense
                        hide-details
                    />
                </div>
                <v-spacer></v-spacer>
                <download-csv
                    :data="records"
                    :name="meter + '_' + type + '.csv'">
                    <v-btn tile depressed small color="accent" class="text-caption" :disabled="!meter">
                        CSV <v-icon small>mdi-download</v-icon>
                    </v-btn>
                </download-csv>
                <v-btn color="primary" tile depressed small @click="edit = true" class="ml-2" :disabled="!meter">
                    <v-icon small>mdi-calendar-edit-outline</v-icon>
                </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
            <div class="text-caption text-center font-weight-medium mr-2">
                {{ type }}
            </div>
            <v-divider></v-divider>
            <v-card-text v-if="open">
                <div class="py-10 text-center" v-if="ready && meter && no_data">
                    <v-icon large>mdi-table-border</v-icon>
                    <p class="subtitle-2 mt-3 mb-0">Sin datos</p>
                </div>
                <v-data-table
                    v-else-if="ready && meter && !no_data"
                    :headers="headers"
                    :items="records"
                    :items-per-page="15"
                ></v-data-table>
                <div class="text-center align-center" v-else>
                    <div class="py-16" v-if="!ready && meter">
                        <v-progress-circular
                            :indeterminate="true"
                            width="5"
                            size="50"
                            color="accent"
                        ></v-progress-circular>
                    </div>
                    <div class="py-10" v-else>
                        <v-icon>mdi-form-select</v-icon>
                        <p class="subtitle-2 mt-3 mb-0">Seleccione un medidor</p>
                    </div>
                </div>
            </v-card-text>
        </v-card>
        <datepicker 
            :open="edit" 
            @close="edit = false"
            @newValues="showSummaryData"/>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import datepicker from './datePicker'

export default {
    name: 'tableReport',
    props: ['meters', 'plant'],
    components: { datepicker },
    computed:{
        ...mapGetters({
            getMeter: 'meters/getMeter'
        })
    },
    data() {
        return {
            open: false,
            meter: null,
            headers: [],
            records: [],
            ready: false,
            edit: false,
            type: '',
            no_data: false
        }
    },
    methods: {
        ...mapActions({ 
            getLastData: 'meters/queryReadings',
            getSummaryData: 'meters/getSummaryData'
        }),
        async showLastData(){
            try {
                this.ready = false
                this.no_data = false
                this.type = 'Ultimas lecturas'
                const meter = this.meters.find( m => m.id === this.meter )
                const response = await this.getLastData({
                    meter: meter.id,
                    query: {
                        len: 60
                    }
                })
                
                if(response.status !== 200) {
                    this.no_data = true
                    this.ready = true
                    return
                }

                const data = response.data.data
                if(data.length === 0) {
                    this.no_data = true
                    this.ready = true
                    return
                }
                
                const props = Object.keys(data[0].data.measurement)
                this.headers = [{ text: 'Tiempo', value: 'timestamp' }]
                props.forEach( prop => 
                    this.headers.push({ text: prop, value: prop })
                )
                
                this.records = data.map( read => {
                    const measurement = read.data.measurement
                    measurement.timestamp = new Date(read.timestamp).toLocaleString()
                    return measurement
                })
                this.ready = true
            } catch (error) {
                console.log(error)
            }
        },
        async showSummaryData(e){
            try {  
                this.ready = false
                this.no_data = false
                this.type = e.type === 'day' 
                    ? e.start.toLocaleDateString()
                    : `${e.start.getMonth() + 1}/${e.start.getFullYear()}`
                const meter = this.meters.find( m => m.id === this.meter )
                const response = await this.getSummaryData({
                    meters: [meter],
                    type: e.type,
                    start: e.start
                })

                const summary = response[0]
                if(summary.status !== 200) {
                    this.no_data = true
                    this.ready = true
                    return
                }

                const data = summary.data.data
                if(data.length === 0) {
                    this.no_data = true
                    this.ready = true
                    return
                }

                const props = Object.keys(data[0].measurements)
                this.headers = [{ text: 'Tiempo', value: 'timestamp' }]
                props.forEach( prop => {
                    this.headers.push({ text: `${prop}-first`, value: `${prop}-first` })
                    this.headers.push({ text: `${prop}-last`, value: `${prop}-last` })
                    this.headers.push({ text: `${prop}-avg`, value: `${prop}-avg` })
                    this.headers.push({ text: `${prop}-max`, value: `${prop}-max` })
                    this.headers.push({ text: `${prop}-min`, value: `${prop}-min` })
                })
                
                this.records = data.map( read => {
                    const measurement = {}
                    const date = e.type === 'day'
                        ? `${read.date.hour}:00:00`
                        : `${read.date.day}/${read.date.month}/${read.date.year}`
                    measurement.timestamp = date

                    const props = Object.keys(read.measurements)
                    props.forEach( prop => {
                        measurement[`${prop}-first`] = read.measurements[prop].first.toFixed(2)
                        measurement[`${prop}-last`] = read.measurements[prop].last.toFixed(2)
                        measurement[`${prop}-avg`] = read.measurements[prop].avg.toFixed(2)
                        measurement[`${prop}-max`] = read.measurements[prop].max.toFixed(2)
                        measurement[`${prop}-min`] = read.measurements[prop].min.toFixed(2)
                    })

                    return measurement
                })
                this.ready = true
                return
            } catch (error) {
                console.log(error)
            }
        },
        reset(){
            this.open = false
            this.meter = null
            this.headers = []
            this.records = []
            this.ready = false
            this.edit = false
            this.type = ''
            this.no_data = false
        }
    },
    watch:{
        meter(){
            if(this.meter){
                this.showLastData()
            }else
                this.open = false
        },
        plant(){
            this.reset()
        }
    }
}
</script>

<style scoped>
    .line{
        border-left: 2px solid var(--v-primary-base);
        height: 13px;
    }
    .title{
        text-transform: uppercase;
        padding: 0px;
        font-size: 0.9rem !important;
        font-weight: bold;
    }
    .active{
        color: var(--v-accent-base) !important;
        background-color: white !important;
        box-shadow: 0 0 16px -9px black;
    }
    .v-chip{
        border-radius: 15px;
        font-size: 12px;
        height: 30px;
    }
    .v-chip:before {
        background-color: unset;
    }
</style>